import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Phone, Loader2, Monitor, MonitorOff, GraduationCap, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' },
  ],
};

const RING_SOUND_DATA_URL =
  // Tiny 1-second beep generated as data URI (mp3-like, browsers vary). Fallback: use Audio API loop.
  '';

const formatDuration = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

// ================================
// Global event bus to start outgoing calls from anywhere (Chat page).
// ================================
let pendingOutgoing = null;
const outgoingListeners = new Set();
export const startOutgoingCall = (params) => {
  pendingOutgoing = params;
  outgoingListeners.forEach((fn) => fn(params));
};

const CallManager = () => {
  const { user, isAuthenticated } = useAuth();

  // ---- state ----
  const [incoming, setIncoming] = useState(null); // call object (callee perspective)
  const [active, setActive] = useState(null);     // active call being displayed (caller or callee)
  const [role, setRole] = useState(null);         // 'caller' | 'callee'
  const [callType, setCallType] = useState('voice');
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [duration, setDuration] = useState(0);
  const [connected, setConnected] = useState(false);
  const [outgoingState, setOutgoingState] = useState(null); // 'ringing' | 'connecting' | 'in_call' | null
  const [screenSharing, setScreenSharing] = useState(false);
  const [trainingPanelOpen, setTrainingPanelOpen] = useState(false);
  const [trainingModules, setTrainingModules] = useState([]);
  const [trainingActive, setTrainingActive] = useState(null); // currently displayed module on both peers
  const [trainingLoading, setTrainingLoading] = useState(false);

  // ---- refs (mutable across handlers without re-rendering) ----
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const pollRef = useRef(null);
  const ringRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const seenCounts = useRef({ caller: 0, callee: 0 });
  const callIdRef = useRef(null);
  const roleRef = useRef(null);
  const stoppedRef = useRef(false);
  const durationTimer = useRef(null);
  const cameraStreamRef = useRef(null); // saved camera stream while screen sharing
  const screenStreamRef = useRef(null);
  const videoSenderRef = useRef(null);
  const trainingIdSeenRef = useRef(null); // last training_module_id known on the call doc
  const pendingIceRef = useRef([]); // ice candidates buffered before we know the call id (caller flow)

  // ---- helpers ----
  const cleanup = useCallback(() => {
    stoppedRef.current = true;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (durationTimer.current) { clearInterval(durationTimer.current); durationTimer.current = null; }
    if (ringRef.current) { try { ringRef.current.pause(); ringRef.current.currentTime = 0; } catch (_) {} ringRef.current = null; }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) {}
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    videoSenderRef.current = null;
    remoteStreamRef.current = null;
    seenCounts.current = { caller: 0, callee: 0 };
    callIdRef.current = null;
    roleRef.current = null;
    trainingIdSeenRef.current = null;
    pendingIceRef.current = [];
    setActive(null);
    setIncoming(null);
    setRole(null);
    setMuted(false);
    setCamOff(false);
    setDuration(0);
    setConnected(false);
    setOutgoingState(null);
    setScreenSharing(false);
    setTrainingPanelOpen(false);
    setTrainingActive(null);
  }, []);

  const startRingTone = useCallback(() => {
    try {
      // Use Web Audio API to play a soft repeating beep (no asset needed)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      const ramp = () => {
        if (stoppedRef.current) {
          try { osc.stop(); ctx.close(); } catch (_) {}
          return;
        }
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.05, now + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
      };
      const id = setInterval(ramp, 1500);
      ramp();
      ringRef.current = { pause: () => { clearInterval(id); try { osc.stop(); ctx.close(); } catch (_) {} } };
    } catch (e) {
      // Audio context not allowed yet; ignore silently.
    }
  }, []);

  const sendLocalIce = useCallback(async (callId, candidate, mySide) => {
    try {
      await api.post(`/chat/calls/${callId}/ice`, { candidate, role: mySide });
    } catch (_) {}
  }, []);

  const buildPeerConnection = useCallback((callId, mySide) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const cand = e.candidate.toJSON();
      const id = callIdRef.current;
      if (id) {
        sendLocalIce(id, cand, mySide);
      } else {
        // Caller flow: candidates may fire before /initiate returns. Buffer them.
        pendingIceRef.current.push(cand);
      }
    };
    pc.ontrack = (e) => {
      const stream = e.streams && e.streams[0] ? e.streams[0] : new MediaStream([e.track]);
      remoteStreamRef.current = stream;
      // Attach via a microtask + a safety retry for race conditions on slow renders
      const attach = () => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
          remoteVideoRef.current.play?.().catch(() => {});
        }
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = stream;
          remoteAudioRef.current.play?.().catch(() => {});
        }
      };
      setTimeout(attach, 50);
      setTimeout(attach, 400);
      setTimeout(attach, 1200);
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (process.env.NODE_ENV === 'development') console.log('[Call] ICE state:', st);
      if (st === 'connected' || st === 'completed') {
        setConnected(true);
        if (!durationTimer.current) {
          const start = Date.now();
          durationTimer.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - start) / 1000));
          }, 1000);
        }
      }
      if (st === 'failed') {
        toast.error('Connexion echouee. Reseaux 4G/5G non compatibles sans serveur TURN.');
        setTimeout(() => {
          if (callIdRef.current) {
            api.post(`/chat/calls/${callIdRef.current}/end`).catch(() => {});
            cleanup();
          }
        }, 500);
      }
    };
    pc.onconnectionstatechange = () => {
      if (process.env.NODE_ENV === 'development') console.log('[Call] PC state:', pc.connectionState);
    };
    return pc;
  }, [sendLocalIce, cleanup]);

  const startPolling = useCallback((callId, mySide) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/chat/calls/${callId}`, {
          params: {
            caller_seen: seenCounts.current.caller,
            callee_seen: seenCounts.current.callee,
          },
        });
        const c = r.data;
        seenCounts.current.caller = c.ice_caller_count || 0;
        seenCounts.current.callee = c.ice_callee_count || 0;

        // Apply remote ICE candidates from the other side
        const otherIce = mySide === 'caller' ? c.ice_callee_new : c.ice_caller_new;
        if (otherIce && otherIce.length && pcRef.current) {
          for (const cand of otherIce) {
            try {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(cand));
            } catch (e) { /* ignore */ }
          }
        }

        // Caller: apply answer once available
        if (mySide === 'caller' && c.status === 'answered' && c.sdp_answer && pcRef.current) {
          if (!pcRef.current.currentRemoteDescription) {
            await pcRef.current.setRemoteDescription(new RTCSessionDescription(c.sdp_answer));
            setOutgoingState('in_call');
            if (ringRef.current) { try { ringRef.current.pause(); } catch (_) {} }
          }
        }

        if (['ended', 'declined', 'missed'].includes(c.status)) {
          if (c.status === 'declined') toast.info('Appel decline');
          if (c.status === 'missed') toast.info('Appel manque');
          cleanup();
          return;
        }

        // Sync training_module_id between peers
        const tid = c.training_module_id || null;
        if (tid !== trainingIdSeenRef.current) {
          trainingIdSeenRef.current = tid;
          if (!tid) {
            setTrainingActive(null);
          } else {
            try {
              const m = await api.get(`/training/modules/${tid}`);
              setTrainingActive(m.data);
              setTrainingPanelOpen(true);
            } catch { /* ignore */ }
          }
        }
      } catch (e) { /* ignore polling errors */ }
    }, 1500);
  }, [cleanup]);

  // ================================
  // OUTGOING (caller flow)
  // ================================
  const handleStartOutgoing = useCallback(async ({ thread_id, callee_id, callee_name, type }) => {
    if (active || incoming) {
      toast.error('Un appel est deja en cours');
      return;
    }
    stoppedRef.current = false;
    setCallType(type);
    setRole('caller'); roleRef.current = 'caller';
    setOutgoingState('connecting');

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === 'video' ? { width: 640, height: 480 } : false,
      });
    } catch (e) {
      toast.error('Acces micro/camera refuse');
      cleanup();
      return;
    }
    localStreamRef.current = stream;
    setTimeout(() => {
      if (localVideoRef.current && type === 'video') {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.play?.().catch(() => {});
      }
    }, 100);

    const pc = buildPeerConnection(null, 'caller');
    pcRef.current = pc;
    stream.getTracks().forEach((t) => {
      const sender = pc.addTrack(t, stream);
      if (t.kind === 'video') videoSenderRef.current = sender;
    });

    let offer;
    try {
      offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: type === 'video' });
      await pc.setLocalDescription(offer);
    } catch (e) {
      toast.error('Erreur initialisation appel');
      cleanup();
      return;
    }

    let resp;
    try {
      resp = await api.post('/chat/calls/initiate', {
        thread_id, callee_id, type, sdp_offer: { type: offer.type, sdp: offer.sdp },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur appel');
      cleanup();
      return;
    }
    const call = resp.data;
    callIdRef.current = call.id;
    // Flush any ICE candidates that were buffered before we knew the call id
    const flushed = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const cand of flushed) {
      sendLocalIce(call.id, cand, 'caller');
    }
    setActive({ ...call, callee_name });
    setOutgoingState('ringing');
    startRingTone();
    startPolling(call.id, 'caller');
  }, [active, incoming, buildPeerConnection, cleanup, sendLocalIce, startPolling, startRingTone]);

  // Listen for outgoing-call events from the rest of the app
  useEffect(() => {
    const fn = (params) => handleStartOutgoing(params);
    outgoingListeners.add(fn);
    return () => outgoingListeners.delete(fn);
  }, [handleStartOutgoing]);

  // ================================
  // INCOMING (callee flow): poll every 3s
  // ================================
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const tick = async () => {
      if (active || incoming) return;
      try {
        const r = await api.get('/chat/calls/incoming');
        const list = r.data || [];
        if (list.length > 0) {
          setIncoming(list[0]);
          setCallType(list[0].type);
          startRingTone();
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [isAuthenticated, user, active, incoming, startRingTone]);

  const handleAcceptIncoming = useCallback(async () => {
    if (!incoming) return;
    if (ringRef.current) { try { ringRef.current.pause(); } catch (_) {} }
    stoppedRef.current = false;
    setRole('callee'); roleRef.current = 'callee';
    callIdRef.current = incoming.id; // set BEFORE PC creation so ICE candidates are routed correctly
    pendingIceRef.current = [];

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: incoming.type === 'video' ? { width: 640, height: 480 } : false,
      });
    } catch (e) {
      toast.error('Acces micro/camera refuse');
      try { await api.post(`/chat/calls/${incoming.id}/decline`); } catch (_) {}
      cleanup();
      return;
    }
    localStreamRef.current = stream;
    setTimeout(() => {
      if (localVideoRef.current && incoming.type === 'video') {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.play?.().catch(() => {});
      }
    }, 100);

    const pc = buildPeerConnection(incoming.id, 'callee');
    pcRef.current = pc;
    stream.getTracks().forEach((t) => {
      const sender = pc.addTrack(t, stream);
      if (t.kind === 'video') videoSenderRef.current = sender;
    });

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(incoming.sdp_offer));
      const ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
      await api.post(`/chat/calls/${incoming.id}/answer`, {
        sdp_answer: { type: ans.type, sdp: ans.sdp },
      });
    } catch (e) {
      toast.error('Echec connexion');
      try { await api.post(`/chat/calls/${incoming.id}/end`); } catch (_) {}
      cleanup();
      return;
    }
    callIdRef.current = incoming.id;
    setActive({ ...incoming });
    setIncoming(null);
    setOutgoingState('in_call');
    startPolling(incoming.id, 'callee');
  }, [incoming, buildPeerConnection, cleanup, startPolling]);

  const handleDeclineIncoming = useCallback(async () => {
    if (!incoming) return;
    try { await api.post(`/chat/calls/${incoming.id}/decline`); } catch (_) {}
    cleanup();
  }, [incoming, cleanup]);

  // ================================
  // CONTROLS
  // ================================
  const handleHangup = useCallback(async () => {
    const id = callIdRef.current;
    if (id) { try { await api.post(`/chat/calls/${id}/end`); } catch (_) {} }
    cleanup();
  }, [cleanup]);

  const toggleMute = () => {
    if (!localStreamRef.current) return;
    const next = !muted;
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  const toggleCamera = () => {
    if (!localStreamRef.current) return;
    const next = !camOff;
    localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = !next; });
    setCamOff(next);
  };

  // Screen share - toggle
  const toggleScreenShare = useCallback(async () => {
    if (callType !== 'video') return;
    if (!videoSenderRef.current) {
      toast.error('Video non initialisee');
      return;
    }
    if (screenSharing) {
      // Stop sharing - revert to camera
      try {
        const camStream = cameraStreamRef.current;
        if (camStream) {
          const camTrack = camStream.getVideoTracks()[0];
          if (camTrack) await videoSenderRef.current.replaceTrack(camTrack);
          if (localVideoRef.current) localVideoRef.current.srcObject = camStream;
        }
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach((t) => t.stop());
          screenStreamRef.current = null;
        }
        cameraStreamRef.current = null;
        setScreenSharing(false);
        toast.success('Partage arrete');
      } catch (e) {
        toast.error('Impossible d\'arreter le partage');
      }
      return;
    }
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15 },
        audio: false,
      });
      const screenTrack = screenStream.getVideoTracks()[0];
      if (!screenTrack) throw new Error('no screen track');
      // Save current camera stream so we can restore
      cameraStreamRef.current = localStreamRef.current;
      screenStreamRef.current = screenStream;
      await videoSenderRef.current.replaceTrack(screenTrack);
      if (localVideoRef.current) localVideoRef.current.srcObject = screenStream;
      setScreenSharing(true);
      // Auto-stop when user presses browser "Stop sharing"
      screenTrack.addEventListener('ended', () => {
        if (cameraStreamRef.current) {
          const camTrack = cameraStreamRef.current.getVideoTracks()[0];
          if (camTrack && videoSenderRef.current) videoSenderRef.current.replaceTrack(camTrack).catch(() => {});
          if (localVideoRef.current) localVideoRef.current.srcObject = cameraStreamRef.current;
        }
        screenStreamRef.current = null;
        cameraStreamRef.current = null;
        setScreenSharing(false);
      });
      toast.success('Partage d\'ecran demarre');
    } catch (e) {
      if (e?.name !== 'NotAllowedError') {
        toast.error('Partage d\'ecran indisponible');
      }
    }
  }, [callType, screenSharing]);

  // Load training modules when panel opens
  const loadTrainingModules = useCallback(async () => {
    setTrainingLoading(true);
    try {
      const r = await api.get('/training/modules');
      setTrainingModules(r.data || []);
    } catch {
      setTrainingModules([]);
    } finally {
      setTrainingLoading(false);
    }
  }, []);

  const openTrainingPanel = useCallback(async () => {
    setTrainingPanelOpen(true);
    await loadTrainingModules();
  }, [loadTrainingModules]);

  const handlePickTraining = async (mod) => {
    if (!callIdRef.current) return;
    try {
      await api.put(`/chat/calls/${callIdRef.current}/training`, { training_module_id: mod?.id || null });
      setTrainingActive(mod);
      trainingIdSeenRef.current = mod?.id || null;
    } catch (e) {
      toast.error('Erreur de synchronisation');
    }
  };

  const closeTraining = async () => {
    await handlePickTraining(null);
  };

  // ================================
  // RENDER
  // ================================
  if (!isAuthenticated) return null;

  // Incoming call modal
  if (incoming) {
    return (
      <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="incoming-call-modal">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
          <div className="bg-gradient-to-br from-[#1B3A4B] to-[#0F2630] text-white p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-white/20 mx-auto flex items-center justify-center mb-3 animate-pulse">
              <span className="text-3xl font-bold">{(incoming.caller_name || '?').charAt(0).toUpperCase()}</span>
            </div>
            <p className="text-sm opacity-80">Appel {incoming.type === 'video' ? 'video' : 'vocal'} entrant</p>
            <h2 className="text-xl font-semibold mt-1">{incoming.caller_name || 'Inconnu'}</h2>
          </div>
          <div className="p-4 flex gap-3">
            <Button
              onClick={handleDeclineIncoming}
              variant="destructive"
              className="flex-1 h-12"
              data-testid="decline-call-btn"
            >
              <PhoneOff className="w-5 h-5 mr-2" /> Refuser
            </Button>
            <Button
              onClick={handleAcceptIncoming}
              className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="accept-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" /> Accepter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active/outgoing call view
  if (active) {
    const otherName = role === 'caller' ? (active.callee_name || 'Destinataire') : (active.caller_name || 'Appelant');
    const isVideo = callType === 'video';
    return (
      <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="active-call-view">
        {/* Remote video (full screen) */}
        <div className="flex-1 relative overflow-hidden">
          {isVideo ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover bg-gray-900"
            />
          ) : (
            <audio ref={remoteAudioRef} autoPlay />
          )}
          {!isVideo && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#D4A745] to-[#B8862F] mx-auto flex items-center justify-center mb-4">
                  <span className="text-5xl font-bold">{(otherName || '?').charAt(0).toUpperCase()}</span>
                </div>
                <h2 className="text-2xl font-semibold">{otherName}</h2>
              </div>
            </div>
          )}

          {/* Header info */}
          <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent text-white">
            <p className="text-sm opacity-80">{otherName}</p>
            <p className="text-xs opacity-60">
              {outgoingState === 'connecting' && 'Connexion...'}
              {outgoingState === 'ringing' && 'Sonnerie...'}
              {connected && formatDuration(duration)}
              {!connected && outgoingState === 'in_call' && 'Connexion en cours...'}
            </p>
          </div>

          {/* Local video (PIP) */}
          {isVideo && (
            <div className="absolute bottom-28 right-4 w-32 h-44 rounded-lg overflow-hidden border-2 border-white/30 bg-gray-900 shadow-lg">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {camOff && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <VideoOff className="w-6 h-6 text-white/50" />
                </div>
              )}
            </div>
          )}

          {!connected && (
            <div className="absolute inset-0 flex items-end justify-center pb-32 text-white pointer-events-none">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-black/80 backdrop-blur-md p-6 flex items-center justify-center gap-4">
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition ${
              muted ? 'bg-red-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            data-testid="toggle-mute-btn"
          >
            {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </button>
          {isVideo && (
            <button
              onClick={toggleCamera}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition ${
                camOff ? 'bg-red-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="toggle-camera-btn"
            >
              {camOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
            </button>
          )}
          {isVideo && (
            <button
              onClick={toggleScreenShare}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition ${
                screenSharing ? 'bg-emerald-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid="toggle-screen-share-btn"
              title={screenSharing ? "Arreter le partage d'ecran" : "Partager mon ecran"}
            >
              {screenSharing ? <MonitorOff className="w-6 h-6" /> : <Monitor className="w-6 h-6" />}
            </button>
          )}
          <button
            onClick={openTrainingPanel}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition ${
              trainingActive ? 'bg-violet-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            data-testid="open-training-btn"
            title="Module de formation"
          >
            <GraduationCap className="w-6 h-6" />
          </button>
          <button
            onClick={handleHangup}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition shadow-lg"
            data-testid="hangup-btn"
          >
            <PhoneOff className="w-7 h-7" />
          </button>
        </div>

        {/* Training panel overlay */}
        {trainingPanelOpen && (
          <div
            className="absolute right-0 top-0 bottom-24 w-full sm:w-96 bg-white shadow-2xl flex flex-col z-10"
            data-testid="training-panel"
          >
            <div className="px-4 py-3 border-b flex items-center justify-between bg-violet-50">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-violet-700" />
                <p className="font-semibold text-violet-900">Module de formation</p>
              </div>
              <button onClick={() => setTrainingPanelOpen(false)} className="p-1 hover:bg-violet-100 rounded">
                <X className="w-4 h-4 text-violet-700" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {trainingActive ? (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-xs uppercase tracking-wide text-violet-600 font-semibold">{trainingActive.category}</span>
                      <h3 className="text-lg font-semibold text-gray-900 mt-0.5">{trainingActive.title}</h3>
                    </div>
                    <Button size="sm" variant="ghost" onClick={closeTraining} data-testid="close-training-btn">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {trainingActive.description && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{trainingActive.description}</p>
                  )}
                  {trainingActive.video_url && (
                    <a
                      href={trainingActive.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-violet-700 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" /> Ouvrir la video tutoriel
                    </a>
                  )}
                  {Array.isArray(trainingActive.steps) && trainingActive.steps.length > 0 && (
                    <ol className="space-y-2 list-decimal list-inside text-sm">
                      {trainingActive.steps.map((s, i) => (
                        <li key={i} className="text-gray-800">{s}</li>
                      ))}
                    </ol>
                  )}
                  {Array.isArray(trainingActive.shortcuts) && trainingActive.shortcuts.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Acces rapides</p>
                      <div className="flex flex-wrap gap-2">
                        {trainingActive.shortcuts.map((sc, i) => (
                          <a
                            key={i}
                            href={sc.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2.5 py-1 rounded-full bg-violet-100 text-violet-800 hover:bg-violet-200 inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" /> {sc.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : trainingLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                </div>
              ) : trainingModules.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-8">
                  <GraduationCap className="w-10 h-10 mx-auto opacity-30 mb-2" />
                  <p>Aucune formation disponible.</p>
                  <p className="text-xs mt-1">L'admin peut en creer dans <a href="/training" target="_blank" rel="noopener noreferrer" className="text-violet-700 underline">Formation</a>.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-2">
                    Selectionnez une formation pour la partager avec votre interlocuteur.
                  </p>
                  {trainingModules.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => handlePickTraining(m)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-violet-400 hover:bg-violet-50 transition"
                      data-testid={`pick-training-${m.id}`}
                    >
                      <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">{m.category}</p>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">{m.title}</p>
                      {m.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{m.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default CallManager;
